import { Song } from 'types/song';
import { routes } from 'app/routing/routesConfig';
import {
  getOpenInNewTabMenuOption,
  useContextMenu,
} from 'app/components/ChakraTableList';
import { useCallback, useMemo, useRef } from 'react';
import { useInfiniteLoader } from 'app/utils/hooks';
import { fetchGlobalSearchEffect } from 'app/store/GlobalSearch/effects';
import { useAppSelector } from 'app/store';
import { getNextSearchLink } from 'app/store/GlobalSearch/selectors';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import {
  getAudioPlayer,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { Box, BoxProps, Text } from '@chakra-ui/react';
import { getColumns } from './utils';
import { globalSearchScrollContainerId } from '../constants';
import { SongResultsRow } from './SongResultsRow';

interface SongResultsProps extends BoxProps {
  songs: Song[];
}

export const SongResults = ({ songs, ...boxProps }: SongResultsProps) => {
  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    listId: globalSearchScrollContainerId,
    dataLength: songs.length,
    nextLinkSelector: getNextSearchLink,
    fetchNext: (next) => fetchGlobalSearchEffect({ next }),
  });

  const tableKey = 'global-search';
  const ref = useRef<HTMLDivElement>(null);
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ totalWidth }), [totalWidth]);
  const contextMenuProps = useContextMenu();

  const currentAudio = useAppSelector(getPlayerCurrentAudio);
  const { isPlaying } = useAppSelector(getAudioPlayer);
  const { loadAudio, playPauseAudio } = useAudioPlayer();
  const handlePlay = useCallback(
    async (song: Song) => {
      if (song.latestVersionAudio) {
        if (currentAudio?.audioGuid === song.latestVersionAudio) {
          playPauseAudio();
        } else {
          await loadAudio({
            audio: [
              {
                audioGuid: song.latestVersionAudio,
                parentGuid: song.guid,
                songStatus: song.status,
                songTitle: song.title,
              },
            ],
          });
        }
      }
    },
    [currentAudio?.audioGuid, loadAudio, playPauseAudio]
  );

  const rowRenderer = useCallback(
    (item: Row<Song>) => {
      const song = item.original;
      const options = [
        ...getOpenInNewTabMenuOption(
          routes.song.path.replace(':guid', song.guid)
        ),
      ];

      const contextHandler = (e) =>
        contextMenuHandler(e, contextMenuProps, options);

      return (
        <SongResultsRow
          key={song.guid}
          song={song}
          handlePlay={handlePlay}
          isPlaying={isPlaying}
          contextHandler={contextHandler}
        />
      );
    },
    [contextMenuProps, handlePlay, isPlaying]
  );

  return (
    <Box {...boxProps}>
      <Text fontSize="md" fontWeight={500} pb={4}>
        Songs
      </Text>
      <StickyTable<Song>
        data-testid="global-search-song-list"
        tableKey={tableKey}
        ref={ref}
        data={songs}
        columns={columns}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
        infiniteScrollProps={infiniteScrollProps}
        loadingMore={loadingMore}
        showHeader={false}
      />
    </Box>
  );
};
